// Copyright (c) StrangeBee 2024

import client from '@Client';
import { actionListRenderer, ActionListRendererParams } from '@Helpers/actionListRenderer';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { ResponderDrawer } from '@Organisms/Responder';
import RouteBuilder from '@Services/RouteBuilder';
import { ListActionType } from '@Types';
import React, { ReactElement } from 'react';
import {
    AlertOutput,
    CaseInput,
    CaseOutput,
    AlertStatusStage,
    CustomStatusMapType,
    UserOutput,
    PermissionsEnum,
} from 'thehive-sdk';
import AlertMergeDrawer from '@Organisms/Alert/AlertMerge/AlertMergeDrawer';
import { CaseCreateManage } from '@Organisms/Case/CaseCreate/CaseCreateManage';
import { AlertBulkEditManage } from '@Organisms/Alert/AlertBulkEdit/AlertBulkEditManage';
import { AlertEventList } from './eventList';
import { ICONS } from '@Constants/iconsApp';
import { AlertChangeStatus } from './AlertChangeStatus/AlertChangeStatus';
import { PermissionExtract } from '@Hooks/useSplitPermissions';

const cycleStage = [AlertStatusStage.NEW, AlertStatusStage.IN_PROGRESS, AlertStatusStage.CLOSED];

export const computeActionList = (
    locale: (...args: any[]) => any,
    history: any, // eslint-disable-line
    alertStatus: CustomStatusMapType,
    eventList: AlertEventList,
    hasPermissions: Record<
        PermissionExtract<[PermissionsEnum.MANAGE_ALERT_RESTART, PermissionsEnum.MANAGE_ALERT_UPDATE]>,
        boolean
    >,
    currentUserData?: UserOutput,
): ActionListRendererParams<AlertOutput> =>
    actionListRenderer<AlertOutput>(
        (
            selectedItems,
            onSuccess,
        ): {
            [key: string]: ListActionType;
        } => {
            const alertListIds = selectedItems.map(({ _id }) => _id);
            const isImported = selectedItems.some(({ caseId }) => caseId);

            const data = (selectedItems[0] || {}) as AlertOutput;

            const isMultiple = alertListIds.length > 1;

            const stageIndex = cycleStage.indexOf((data.stage || '') as AlertStatusStage);
            const alertStatusStageValid = Object.values(alertStatus)
                .flat()
                .reduce((acc, curr) => {
                    const stagePosition = cycleStage.indexOf((curr.stage || '') as AlertStatusStage);
                    if (
                        curr.stage === AlertStatusStage.IMPORTED ||
                        (stageIndex >= stagePosition && data.stage !== AlertStatusStage.CLOSED) ||
                        (curr.stage !== AlertStatusStage.IN_PROGRESS && data.stage === AlertStatusStage.CLOSED)
                    ) {
                        return acc;
                    }

                    if (!acc[curr.stage]) {
                        acc[curr.stage] = [];
                    }
                    acc[curr.stage].push(curr);
                    return acc;
                }, {} as CustomStatusMapType);

            return {
                assign: {
                    label: locale('common.assigntome'),
                    icon: <ICONS.UserPlus />,
                    hidden: !selectedItems?.some(({ assignee }) => assignee !== currentUserData?.login),
                    scope: 'both',
                    query: () => {
                        eventList.onBulkEdit({ assignee: currentUserData?.login }, alertListIds).then(() => {
                            onSuccess?.();
                            openMessage('success', locale('notification.alert.assignee'));
                        });
                    },
                },
                edit: {
                    label: locale('alert.actions.edit.label'),
                    icon: <ICONS.EditIcon />,
                    scope: 'list',
                    CustomComponent: (triggerElem): ReactElement => {
                        const firstElem = selectedItems?.find(Boolean);

                        const changeStatus = selectedItems?.every(
                            ({ stage }) => stage !== AlertStatusStage.IMPORTED && stage === firstElem?.stage,
                        );
                        const currentStage = firstElem?.stage as AlertStatusStage;

                        const idsAlertsNew = selectedItems
                            ?.filter(({ stage }) => stage === AlertStatusStage.NEW)
                            ?.map(({ _id }) => _id);
                        const idsAlertsOther = selectedItems
                            ?.filter(({ stage }) => stage !== AlertStatusStage.NEW)
                            ?.map(({ _id }) => _id);

                        return (
                            <AlertBulkEditManage
                                triggerElem={triggerElem}
                                changeStatus={changeStatus}
                                statusList={{ [currentStage]: alertStatus[currentStage] }}
                                onEdit={(values) => {
                                    Promise.all([
                                        eventList.onBulkEdit(values, idsAlertsNew, true),
                                        eventList.onBulkEdit(values, idsAlertsOther),
                                    ]).then(() => {
                                        onSuccess?.(locale('notification.alert.edit'));
                                    });
                                }}
                            />
                        );
                    },
                },
                open: {
                    scope: 'single',
                    disabled:
                        !alertStatusStageValid?.InProgress ||
                        data.stage === AlertStatusStage.IMPORTED ||
                        !hasPermissions.reopenAlert,
                    CustomComponent: (triggerElem): ReactElement => (
                        <AlertChangeStatus
                            triggerElem={triggerElem}
                            onEdit={(alertData) =>
                                eventList?.onEdit(alertData, data._id, data.stage === AlertStatusStage.NEW).then(() => {
                                    onSuccess?.(locale('notification.alert.start'));
                                })
                            }
                            statusList={{
                                [AlertStatusStage.IN_PROGRESS]: alertStatusStageValid?.InProgress || [],
                            }}
                            initialValues={{
                                status: alertStatusStageValid?.[AlertStatusStage.IN_PROGRESS]?.[0].value || '',
                                summary: data.summary,
                                customFields: data.customFields,
                                assignee: data?.assignee || currentUserData?.login,
                            }}
                        />
                    ),
                    ...(data.stage !== AlertStatusStage.CLOSED
                        ? {
                              label: locale('alert.actions.start.label'),
                              icon: <ICONS.StartIcon />,
                          }
                        : {
                              label: locale('alert.actions.restart.label'),
                              icon: <ICONS.ReloadOutlined />,
                          }),
                },
                close: {
                    label: locale('alert.actions.close.label'),
                    icon: <ICONS.CloseIcon />,
                    scope: 'single',
                    disabled: !alertStatusStageValid?.Closed || data.stage === AlertStatusStage.IMPORTED,
                    CustomComponent: (triggerElem): ReactElement => (
                        <AlertChangeStatus
                            triggerElem={triggerElem}
                            onEdit={(alertData) =>
                                eventList?.onEdit(alertData, data._id, data.stage === AlertStatusStage.NEW).then(() => {
                                    onSuccess?.(locale('notification.alert.close'));
                                })
                            }
                            statusList={{
                                [AlertStatusStage.CLOSED]: alertStatusStageValid?.Closed || [],
                            }}
                            initialValues={{
                                status: alertStatusStageValid?.[AlertStatusStage.CLOSED]?.[0].value || '',
                                summary: data.summary,
                                customFields: data.customFields,
                                assignee: data?.assignee || currentUserData?.login,
                            }}
                            useCustomField
                        />
                    ),
                },
                bulkClose: {
                    label: locale('alert.actions.close.label'),
                    icon: <ICONS.CloseIcon />,
                    scope: 'list',
                    hidden: selectedItems.some(({ stage }) =>
                        ([AlertStatusStage.CLOSED, AlertStatusStage.IMPORTED] as string[]).includes(stage || ''),
                    ),
                    CustomComponent: (triggerElem): ReactElement => {
                        const idsAlerts = selectedItems.map(({ _id }) => _id);

                        return (
                            <AlertChangeStatus
                                triggerElem={triggerElem}
                                onEdit={(alertData) =>
                                    eventList.onBulkEdit(alertData, idsAlerts).then(() => {
                                        onSuccess?.(locale('notification.alert.close'));
                                    })
                                }
                                statusList={{
                                    [AlertStatusStage.CLOSED]: alertStatusStageValid?.Closed || [],
                                }}
                                initialValues={{
                                    status: (alertStatusStageValid?.Closed?.[0].value || '') as string,
                                    summary: '',
                                    customFields: data.customFields,
                                }}
                            />
                        );
                    },
                },
                follow: {
                    label: locale('alert.actions.follow.label'),
                    hidden: selectedItems.some(({ follow }) => follow),
                    icon: <ICONS.EyeIcon />,
                    query: (): void => {
                        eventList.onMultipleFollow(alertListIds).then(() => onSuccess());
                    },
                },
                unfollow: {
                    label: locale('alert.actions.unfollow.label'),
                    hidden: selectedItems.some(({ follow }) => !follow),
                    icon: <ICONS.EyeInvisibleIcon />,
                    query: (): void => {
                        eventList.onMultipleUnfollow(alertListIds).then(() => onSuccess());
                    },
                },
                createCaseFromSeveralAlerts: {
                    label: locale('alert.actions.create-case-several.label'),
                    hidden: isImported,
                    icon: <ICONS.CreateIcon />,
                    scope: 'both',
                    CustomComponent: (triggerElem): ReactElement => (
                        <CaseCreateManage
                            defaultCaseTemplate={data?.caseTemplate}
                            initialValues={isMultiple ? {} : data}
                            title={locale('alert.actions.create-case-several.label')}
                            triggerElem={triggerElem}
                            onImport={(formData) => {
                                return client.CaseController.import(formData)
                                    .then(({ case: { _id: caseId } }) =>
                                        client.AlertController.mergeSeveralIntoCase({
                                            alertIds: alertListIds,
                                            caseId,
                                        }),
                                    )
                                    .then(({ _id: caseId }) => {
                                        openMessage('success', locale('notification.alert.create-case-several'));
                                        history.push(RouteBuilder.compute('cases.details', { caseId }));
                                    })
                                    .catch(handleNotificationError);
                            }}
                            onMispImport={(formData) => {
                                return client.MispController.import(formData)
                                    .then(({ _id: caseId }) =>
                                        client.AlertController.mergeSeveralIntoCase({
                                            alertIds: alertListIds,
                                            caseId,
                                        }),
                                    )
                                    .then(({ _id: caseId }) => {
                                        openMessage('success', locale('notification.alert.create-case-several'));
                                        history.push(RouteBuilder.compute('cases.details', { caseId }));
                                    })
                                    .catch(handleNotificationError);
                            }}
                            onCaseCreate={(caseData) => {
                                let creation: Promise<CaseOutput | void> | null = null;
                                if (isMultiple) {
                                    creation = client.CaseController.create<CaseInput, CaseOutput>(caseData).then(
                                        ({ _id: caseId }) => eventList.onMergeSeveralIntoCase(alertListIds, caseId),
                                    );
                                } else {
                                    creation = client.AlertController.createCase(alertListIds[0], caseData);
                                }
                                return creation
                                    .then((data) => {
                                        openMessage('success', locale('notification.alert.create-case-several'));
                                        if (data) {
                                            const { _id: caseId } = data;
                                            history.push(RouteBuilder.compute('cases.details', { caseId }));
                                        }
                                        return data;
                                    })
                                    .catch(handleNotificationError);
                            }}
                        />
                    ),
                },
                mergeAlertsIntoCase: {
                    label: locale('alert.actions.merge-alerts.label'),
                    hidden: isImported,
                    icon: <ICONS.MergeIcon />,
                    scope: 'both',
                    CustomComponent: (triggerElem): ReactElement => (
                        <AlertMergeDrawer
                            listCasesForMerge={(filter) =>
                                client.CaseController.list<CaseOutput[]>({
                                    selector: {
                                        head: [{ name: 'listCase' }],
                                        name: 'get-cases-for-merge',
                                        extraData: ['status'],
                                    },
                                    filters: [filter],
                                    paginationOptions: { page: 0, itemsPerPage: 20 },
                                })
                            }
                            triggerElem={triggerElem}
                            onMergeAlertsIntoCase={(caseId) =>
                                eventList
                                    .onMergeSeveralIntoCase(alertListIds, caseId)
                                    .then(() => {
                                        history.push(RouteBuilder.compute('cases.details', { caseId }));
                                    })
                                    .catch(handleNotificationError)
                            }
                        />
                    ),
                },

                responders: {
                    label: locale('alert.actions.responders.label'),
                    icon: <ICONS.RunResponderIcon />,
                    scope: 'single',
                    CustomComponent: (triggerElem): ReactElement => (
                        <ResponderDrawer
                            objectId={alertListIds.find(Boolean) || ''}
                            objectType="alert"
                            triggerElem={triggerElem}
                        />
                    ),
                },
                delete: {
                    label: locale('alert.actions.delete.label'),
                    icon: <ICONS.DeleteIcon />,
                    confirm: true,
                    message: locale(isMultiple ? 'alert.actions.delete.multiple' : 'alert.actions.delete.single'),
                    scope: 'list',
                    query: (): void => {
                        eventList
                            .onBulkDelete(alertListIds)
                            .then(() => onSuccess(true))
                            .catch(handleNotificationError);
                    },
                },
            };
        },
    );
